import { useEffect, useRef, useState } from "react";
import { Sparkles, ChefHat, Heart, Star } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const Timeline = () => {
  const { t } = useLanguage();
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.25 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => {
      if (ref.current) observer.unobserve(ref.current);
    };
  }, []);

  const milestones = [
    {
      icon: <Sparkles className="w-5 h-5" />,
      label: t("about.timeline.1.label"),
      title: t("about.timeline.1.title"),
      description: t("about.timeline.1.desc"),
    },
    {
      icon: <ChefHat className="w-5 h-5" />,
      label: t("about.timeline.2.label"),
      title: t("about.timeline.2.title"),
      description: t("about.timeline.2.desc"),
    },
    {
      icon: <Heart className="w-5 h-5" />,
      label: t("about.timeline.3.label"),
      title: t("about.timeline.3.title"),
      description: t("about.timeline.3.desc"),
    },
    {
      icon: <Star className="w-5 h-5" />,
      label: t("about.timeline.4.label"),
      title: t("about.timeline.4.title"),
      description: t("about.timeline.4.desc"),
    },
  ];

  const CardContent = ({ m, align }: { m: typeof milestones[number]; align: "top" | "bottom" }) => (
    <div
      className={`group relative px-4 ${
        align === "top" ? "text-center pb-6" : "text-center pt-6"
      }`}
    >
      <div className="p-5 rounded-xl bg-card/60 backdrop-blur-md border border-gold/20 transition-all duration-500 group-hover:border-gold/50 group-hover:-translate-y-1 group-hover:shadow-[0_15px_40px_rgba(212,175,55,0.18)]">
        <p className="text-gold text-[11px] font-medium tracking-[0.3em] uppercase mb-2 animate-text-glow">
          {m.label}
        </p>
        <h3 className="text-lg font-serif font-semibold text-foreground mb-2 transition-colors group-hover:text-gold">
          {m.title}
        </h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{m.description}</p>
      </div>
    </div>
  );

  return (
    <div ref={ref} className="max-w-6xl mx-auto py-8">
      {/* DESKTOP — horizontal */}
      <div className="hidden md:block relative">
        <div
          className="grid grid-cols-4 items-center"
          style={{ gridTemplateRows: "1fr auto 1fr" }}
        >
          {/* Top row — milestones 1 & 3 */}
          {milestones.map((m, i) => (
            <div
              key={`top-${i}`}
              className="row-start-1 self-end transition-all duration-700"
              style={{
                gridColumnStart: i + 1,
                opacity: isVisible && i % 2 === 0 ? 1 : 0,
                transform: isVisible && i % 2 === 0 ? "translateY(0)" : "translateY(20px)",
                transitionDelay: `${600 + i * 150}ms`,
                visibility: i % 2 === 0 ? "visible" : "hidden",
              }}
            >
              <CardContent m={m} align="top" />
              {/* connector down to line */}
              <div className="mx-auto w-px h-6 bg-gradient-to-b from-gold/40 to-gold" />
            </div>
          ))}

          {/* Middle row — line + nodes */}
          <div className="row-start-2 col-span-4 relative h-12">
            {/* The gold line */}
            <div
              className="absolute top-1/2 left-0 right-0 h-px -translate-y-1/2 bg-gradient-to-r from-transparent via-gold to-transparent origin-left transition-transform duration-[1100ms] ease-out"
              style={{ transform: `scaleX(${isVisible ? 1 : 0}) translateY(-50%)` }}
            />
            {/* Nodes */}
            <div className="absolute inset-0 grid grid-cols-4">
              {milestones.map((m, i) => (
                <div key={`node-${i}`} className="flex items-center justify-center">
                  <div
                    className="relative transition-all duration-500"
                    style={{
                      opacity: isVisible ? 1 : 0,
                      transform: isVisible ? "scale(1)" : "scale(0.4)",
                      transitionDelay: `${500 + i * 150}ms`,
                    }}
                  >
                    <div className="absolute inset-0 rounded-full bg-gold/40 blur-lg animate-pulse" />
                    <div className="relative w-12 h-12 rounded-full bg-background border-2 border-gold flex items-center justify-center text-gold shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all duration-300 hover:shadow-[0_0_30px_rgba(212,175,55,0.7)] hover:scale-110">
                      {m.icon}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom row — milestones 2 & 4 */}
          {milestones.map((m, i) => (
            <div
              key={`bot-${i}`}
              className="row-start-3 self-start transition-all duration-700"
              style={{
                gridColumnStart: i + 1,
                opacity: isVisible && i % 2 === 1 ? 1 : 0,
                transform: isVisible && i % 2 === 1 ? "translateY(0)" : "translateY(-20px)",
                transitionDelay: `${600 + i * 150}ms`,
                visibility: i % 2 === 1 ? "visible" : "hidden",
              }}
            >
              {/* connector up to line */}
              <div className="mx-auto w-px h-6 bg-gradient-to-t from-gold/40 to-gold" />
              <CardContent m={m} align="bottom" />
            </div>
          ))}
        </div>
      </div>

      {/* MOBILE — vertical fallback */}
      <div className="md:hidden relative">
        <div
          className="absolute top-0 bottom-0 left-6 w-px bg-gradient-to-b from-transparent via-gold/60 to-transparent origin-top transition-transform duration-[1100ms] ease-out"
          style={{ transform: `scaleY(${isVisible ? 1 : 0})` }}
        />
        <div className="space-y-8">
          {milestones.map((m, i) => (
            <div
              key={`m-${i}`}
              className="relative pl-16 transition-all duration-700"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateX(0)" : "translateX(-20px)",
                transitionDelay: `${400 + i * 150}ms`,
              }}
            >
              <div className="absolute top-2 left-0 z-10">
                <div className="relative">
                  <div className="absolute inset-0 rounded-full bg-gold/40 blur-md animate-pulse" />
                  <div className="relative w-12 h-12 rounded-full bg-background border-2 border-gold flex items-center justify-center text-gold shadow-[0_0_15px_rgba(212,175,55,0.4)]">
                    {m.icon}
                  </div>
                </div>
              </div>
              <div className="p-5 rounded-xl bg-card/60 backdrop-blur-md border border-gold/20">
                <p className="text-gold text-[11px] font-medium tracking-[0.3em] uppercase mb-2 animate-text-glow">
                  {m.label}
                </p>
                <h3 className="text-lg font-serif font-semibold text-foreground mb-2">
                  {m.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{m.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Timeline;